import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faInfoCircle, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from './ThemeContext';

type ToastKind = 'success' | 'error' | 'info';
interface ToastMsg { id: number; kind: ToastKind; title: string; body?: string }

interface ToastCtx {
  show: (kind: ToastKind, title: string, body?: string) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
}

const Ctx = createContext<ToastCtx>({
  show: () => {}, success: () => {}, error: () => {}, info: () => {},
});

const ICON: Record<ToastKind, IconDefinition> = {
  success: faCheckCircle,
  error: faExclamationCircle,
  info: faInfoCircle,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const dismiss = useCallback(() => {
    setVisible(false);
    if (removeTimer.current) clearTimeout(removeTimer.current);
    removeTimer.current = setTimeout(() => setToast(null), 200);
  }, []);

  const show = useCallback((kind: ToastKind, title: string, body?: string) => {
    idRef.current += 1;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (removeTimer.current) clearTimeout(removeTimer.current);
    setToast({ id: idRef.current, kind, title, body });
    requestAnimationFrame(() => setVisible(true));
    hideTimer.current = setTimeout(dismiss, 2800);
  }, [dismiss]);

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (removeTimer.current) clearTimeout(removeTimer.current);
  }, []);

  const value: ToastCtx = {
    show,
    success: (t, b) => show('success', t, b),
    error: (t, b) => show('error', t, b),
    info: (t, b) => show('info', t, b),
  };
  const toneColor: Record<ToastKind, string> = {
    success: colors.success,
    error: colors.danger,
    info: colors.accentDark,
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
            left: 12,
            right: 12,
            zIndex: 99999,
            pointerEvents: 'none',
          }}
        >
          <div
            onClick={dismiss}
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(-20px)',
              transition: 'opacity 200ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.card,
              border: `1px solid ${colors.cardBorder}`,
              borderLeft: `4px solid ${toneColor[toast.kind]}`,
              borderRadius: 12,
              padding: 14,
              minHeight: 56,
              boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            <FontAwesomeIcon icon={ICON[toast.kind]} style={{ color: toneColor[toast.kind], fontSize: 20, marginRight: 12 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{toast.title}</div>
              {!!toast.body && <div style={{ fontSize: 12, marginTop: 2, color: colors.textSub }}>{toast.body}</div>}
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
